import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import api from "@/lib/api";

/**
 * Home page "What They're Saying" section.
 * Left: eyebrow + section title. Right: large average rating badge.
 * Below: horizontal row of individual review cards (scrolls on mobile).
 * Data sourced from GET /api/reviews (average auto-computed on the server).
 */
export default function CustomerReviews() {
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });

  useEffect(() => {
    api.get("/reviews")
      .then(({ data }) => setData(data))
      .catch(() => setData({ reviews: [], average: 0, count: 0 }));
  }, []);

  if (!data.reviews.length) return null;

  const avg = data.average || 0;
  const filledStars = Math.round(avg);

  return (
    <section
      style={{ order: 8 }}
      className="mx-auto max-w-[1440px] w-full px-5 sm:px-10 py-10 sm:py-16 overflow-hidden"
      data-testid="home-reviews"
    >
      {/* Header row */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="overline flex items-center gap-1.5" data-testid="reviews-eyebrow">
            <Star size={11} className="fill-sj-goldDark text-sj-goldDark" />
            Trusted by our customers
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-light mt-2 text-sj-ink">
            What They're Saying
          </h2>
          <p className="mt-2 text-sm text-sj-inkSoft max-w-md">
            Real notes from real Star Jewels wearers — because a piece feels different when someone loves theirs.
          </p>
        </div>

        {/* Average rating badge */}
        <div
          className="self-start md:self-end flex flex-col items-center md:items-end shrink-0 pt-2"
          data-testid="reviews-avg"
        >
          <div className="font-display text-5xl sm:text-6xl font-light text-sj-goldDark leading-none">
            {avg.toFixed(1)}
          </div>
          <div className="mt-2 flex items-center gap-0.5" aria-label={`Average ${avg} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                size={14}
                strokeWidth={1.5}
                className={n <= filledStars ? "fill-sj-goldDark text-sj-goldDark" : "text-sj-goldDark/30"}
              />
            ))}
          </div>
          <div className="mt-1.5 text-[10px] tracking-[0.22em] uppercase text-sj-inkSoft">
            Avg. Rating
          </div>
        </div>
      </div>

      {/* Review cards row */}
      <div className="mt-8 sm:mt-10 flex gap-4 sm:gap-5 overflow-x-auto md:overflow-visible md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 snap-x md:snap-none pb-2 md:pb-0 max-w-full">
        {data.reviews.slice(0, 8).map((r, i) => (
          <motion.article
            key={r.id}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
            className="flex-shrink-0 w-[85%] sm:w-[60%] md:w-auto snap-start rounded-xl border border-sj-gold/25 bg-white p-5 sm:p-6 flex flex-col shadow-[0_1px_0_rgba(0,0,0,0.02)]"
            data-testid={`review-card-${r.id}`}
          >
            {/* Stars */}
            <div className="flex items-center gap-0.5" aria-label={`${r.rating} out of 5`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  size={13}
                  strokeWidth={1.5}
                  className={n <= r.rating ? "fill-sj-goldDark text-sj-goldDark" : "text-sj-goldDark/25"}
                />
              ))}
            </div>

            {/* Quote */}
            <blockquote className="mt-3 text-[13.5px] leading-relaxed text-sj-ink font-normal flex-1">
              <span className="text-sj-goldDark font-display text-lg leading-none pr-0.5">“</span>
              {r.text}
              <span className="text-sj-goldDark font-display text-lg leading-none pl-0.5">”</span>
            </blockquote>

            {/* Attribution */}
            <div className="mt-5 pt-4 border-t border-sj-ink/8">
              <div className="text-sm font-medium text-sj-ink" data-testid={`review-name-${r.id}`}>
                {r.customer_name}
              </div>
              <div className="mt-0.5 text-[11px] uppercase tracking-[0.14em] text-sj-inkSoft">
                {r.product_name}
              </div>
              <div className="mt-0.5 text-[11px] text-sj-muted">{r.location}</div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
